import React from 'react';
import { ThermometerSun, Settings, Wallet, Zap, Home } from 'lucide-react';
import { Reveal } from '../ui-custom/Reveal';
import { TiltCard } from '../ui-custom/TiltCard';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme } from '../../utils/theme';

const colorMap = {
  red: theme.colors.primary,
  orange: theme.colors.secondary,
  teal: theme.colors.accent
};

const ProblemCard = ({ title, text, color, IconComponent }) => {
  const colorClass = colorMap[color];
  
  return (
    <div 
      className="h-full p-8 rounded-2xl border bg-gradient-to-br from-white/5 to-transparent text-left hover:bg-white/10 hover:border-white/30 transition-all duration-300 group shadow-2xl"
      style={{ borderColor: theme.colors.border }}
    >
      <div className="mb-6 transform group-hover:scale-110 group-hover:-translate-y-2 transition-transform duration-300">
        <IconComponent 
          size={50} 
          className={`drop-shadow-[0_0_10px_currentColor]`}
          style={{ color: colorClass }}
        />
      </div>
      <h3 className="text-3xl font-bold text-white mb-4">{title}</h3>
      <p className="text-lg leading-relaxed text-gray-400 group-hover:text-gray-300 transition-colors">
        {text}
      </p>
    </div>
  );
};

const StatCard = ({ value, label, desc, color, delay, percent }) => {
  return (
    // J'ajoute 'h-full' ici pour que le Reveal prenne toute la hauteur disponible dans la grille
    <Reveal delay={delay} className="h-full">
      <div className="relative group h-full"> {/* Ajout de h-full ici aussi */}
        
        {/* Glow effect */}
        <div 
          className="absolute inset-0 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
          style={{ backgroundColor: color }}
        />
        
        <div 
          // Ajout de 'h-full' et 'flex flex-col' pour forcer la hauteur égale et la structure
          className="relative p-8 md:p-10 rounded-3xl border border-white/5 backdrop-blur-sm transition-all duration-500 group-hover:scale-[1.02] group-hover:border-white/20 h-full flex flex-col"
          style={{ 
            backgroundColor: 'rgba(10, 10, 10, 0.8)'
          }}
        >

          {/* Value */}
          <div 
            className="text-6xl md:text-7xl font-black mb-2 tracking-tighter transition-all duration-500"
            style={{ color }}
          >
            {value}
          </div>
          
          {/* Label */}
          <div className="text-xl md:text-2xl font-bold text-white mb-3">
            {label}
          </div>
          
          {/* Description - 'flex-grow' permet de pousser le bas si besoin, mais ici c'est optionnel */}
          <p className="text-gray-400 text-base leading-relaxed mb-4"> {/* Ajout d'un petit mb-4 pour ne pas coller à la ligne */}
            {desc}
          </p>

          {/* Decorative line */}
          {/* CHANGEMENT ICI: Remplacement de 'left-0' par 'left-8 md:left-10' pour s'aligner avec le padding */}
          <div 
            className="absolute bottom-0 left-8 md:left-4 h-1 rounded-t-3xl transition-all duration-500 group-hover:w-[calc(100%-4rem)]" // J'ai ajusté l'animation de largeur pour qu'elle reste jolie
            style={{ 
              backgroundColor: color,
              width: percent 
            }}
          />
        </div>
      </div>
    </Reveal>
  );
};

export const ProblemSection = () => {
  const { t } = useLanguage();
  
  const problems = [
    { icon: ThermometerSun, title: t.problem.card1Title, text: t.problem.card1Text, color: 'red' },
    { icon: Settings, title: t.problem.card2Title, text: t.problem.card2Text, color: 'orange' },
    { icon: Wallet, title: t.problem.card3Title, text: t.problem.card3Text, color: 'teal' }
  ];
  
  return (
    <section 
      className="py-20 relative z-10 border-t" 
      style={{ borderColor: theme.colors.border, backgroundColor: '#050505' }}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(231,165,95,0.05),_transparent_70%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Title */}
        <Reveal>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
              {t.problem.title}
            </h2>
            <p className="text-xl" style={{ color: theme.colors.textMuted }}>
              {t.problem.subtitle}
            </p>
          </div>
        </Reveal>
        
        {/* Problem cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
          {problems.map((problem, idx) => (
            <Reveal key={idx} delay={100 * (idx + 1)} className="h-full">
              <TiltCard className="h-full">
                <ProblemCard 
                  title={problem.title}
                  text={problem.text}
                  color={problem.color}
                  IconComponent={problem.icon}
                />
              </TiltCard>
            </Reveal>
          ))}
        </div>

        {/* Statistics Section */}
        <Reveal>
          <div className="text-center mb-20">
            <h3 className="text-2xl md:text-4xl font-bold text-white mb-2">
              {t.problem.statsTitle}
            </h3>
            <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: theme.colors.primary }} />
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
          <StatCard 
            value={t.problem.stat1Value}
            label={t.problem.stat1Label}
            desc={t.problem.stat1Desc}
            color={theme.colors.secondary}
            delay={200}
            percent={'54%'}
          />
          <StatCard 
            value={t.problem.stat2Value}
            label={t.problem.stat2Label}
            desc={t.problem.stat2Desc}
            color={theme.colors.accent}
            delay={400}
            percent={'15%'}
          />
        </div>
      </div>
    </section>
  );
};
